/**
 * Passkey Registration Dialog
 * @description Passkey 등록 다이얼로그 컴포넌트
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/primitives';
import { usePasskey } from '@/hooks/usePasskey';
import { Key, Loader2 } from 'lucide-react';
import type { Operator } from '@/services/operatorApi';

interface PasskeyRegistrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adminUserId: string;
  operators: Operator[];
  onSuccess?: () => void;
}

export const PasskeyRegistrationDialog: React.FC<PasskeyRegistrationDialogProps> = ({
  open,
  onOpenChange,
  adminUserId: initialAdminUserId,
  operators,
  onSuccess,
}) => {
  const [adminUserId, setAdminUserId] = useState(initialAdminUserId);

  useEffect(() => {
    if (open) {
      setAdminUserId(initialAdminUserId || (operators[0]?.username ?? ''));
    }
  }, [open, initialAdminUserId, operators]);
  const { register, isLoading, error } = usePasskey();
  const [success, setSuccess] = useState(false);

  const handleRegister = async () => {
    if (!adminUserId.trim()) {
      return;
    }

    try {
      await register(adminUserId.trim());
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onOpenChange(false);
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);
    } catch (err) {
      // Error is handled by usePasskey hook
      console.error('Passkey registration failed:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Passkey 등록</DialogTitle>
          <DialogDescription>
            관리자를 선택하고 Passkey를 등록하세요. 등록 후 해당 기기에서 Passkey로 로그인할 수 있습니다.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="adminUserId" className="text-sm font-medium">
              관리자 <span className="text-red-500">*</span>
            </label>
            <Select
              value={adminUserId}
              onValueChange={setAdminUserId}
              disabled={isLoading || success || operators.length === 0}
            >
              <SelectTrigger id="adminUserId">
                <SelectValue placeholder="관리자 선택" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op.id} value={op.username}>
                    {op.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {operators.length === 0 && (
              <p className="text-xs text-muted-foreground">등록된 관리자가 없습니다. 먼저 운영자 관리에서 관리자를 추가하세요.</p>
            )}
          </div>
          {error && (
            <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">Passkey가 성공적으로 등록되었습니다!</p>
            </div>
          )}
          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <p className="text-xs text-muted-foreground">
              <strong>참고:</strong> Passkey 등록 시 브라우저에서 인증 기기 선택을 요청할 수 있습니다.
              Windows Hello, Touch ID, Face ID 등을 사용할 수 있습니다.
            </p>
            <p className="text-xs text-muted-foreground">
              <strong>다중 기기:</strong> 스마트폰에서도 사용하려면 각 기기에서 별도로 Passkey를 등록하세요.
              또는 브라우저에서 &quot;다른 기기 동기화&quot; 옵션을 선택하면 iCloud/Google 계정으로 다른 기기에서 사용할 수 있습니다.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          <Button onClick={handleRegister} disabled={isLoading || success || !adminUserId.trim() || operators.length === 0}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                등록 중...
              </span>
            ) : success ? (
              '등록 완료'
            ) : (
              <span className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Passkey 등록
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
